import React, {useContext, useEffect, useState, useCallback} from "react";
import {Link} from "react-router-dom";
import StyleContext from "../../contexts/StyleContext";
import {fetchBlogs} from "../../services/api";
import {timeAgo} from "../../utils/timeAgo";
import GlobalLoader from "../../components/loader/GlobalLoader";
import "./Blog.scss";

const PER_PAGE = 15;

const BlogList = () => {
  const {isDark} = useContext(StyleContext);
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [view, setView] = useState(
    () => localStorage.getItem("blog_view") || "grid"
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBlogs({page, perPage: PER_PAGE});
      setBlogs(data.blogs || []);
      setTotal(data.total || 0);
      setOffline(!!data.offline);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const setViewMode = mode => {
    setView(mode);
    localStorage.setItem("blog_view", mode);
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <main className={`blog-page ${isDark ? "dark" : "light"}`}>
      <div className="blog-page__header">
        <div>
          <h1 className="blog-page__title">Blog</h1>
          <p className="blog-page__subtitle">
            Notes on Salesforce, Apex, LWC and full-stack development.
          </p>
        </div>
        <div className="blog-page__view-toggle" role="tablist">
          <button
            type="button"
            className={view === "grid" ? "active" : ""}
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
          >
            ▦ Grid
          </button>
          <button
            type="button"
            className={view === "list" ? "active" : ""}
            onClick={() => setViewMode("list")}
            aria-label="List view"
          >
            ☰ List
          </button>
        </div>
      </div>

      {offline && (
        <div className="blog-page__notice">
          Showing offline content — live blog feed is temporarily unavailable.
        </div>
      )}

      {loading ? (
        <GlobalLoader inline label="Loading posts" />
      ) : blogs.length === 0 ? (
        <div className="blog-page__state">No posts yet.</div>
      ) : (
        <section className={`blog-list blog-list--${view}`}>
          {blogs.map(b => (
            <BlogCard key={b.id} blog={b} view={view} isDark={isDark} />
          ))}
        </section>
      )}

      {totalPages > 1 && (
        <nav className="blog-page__pagination" aria-label="Pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ‹ Prev
          </button>
          {Array.from({length: totalPages}, (_, i) => i + 1).map(n => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={n === page ? "active" : ""}
            >
              {n}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next ›
          </button>
        </nav>
      )}
    </main>
  );
};

const BlogCard = ({blog, view}) => {
  const cover =
    blog.cover_image ||
    "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200";
  return (
    <article className={`blog-card blog-card--${view}`}>
      <Link to={`/blog/${blog.id}`} className="blog-card__link">
        <div
          className="blog-card__cover"
          style={{backgroundImage: `url(${cover})`}}
        />
        <div className="blog-card__body">
          <h2 className="blog-card__title">{blog.title}</h2>
          <p className="blog-card__excerpt">{blog.excerpt}</p>
          <div className="blog-card__meta">
            <span>{blog.author || "Rashid Ahmad"}</span>
            <span>•</span>
            <span>{timeAgo(blog.created_at)}</span>
            <span>•</span>
            <span>{blog.views || 0} views</span>
            <span>•</span>
            <span>♥ {blog.likes || 0}</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogList;
